import type {
  AnalyticsEventType,
  DochatAuthProvider,
  DochatAuthPublicReason,
  APIDochatConfig,
  APIDochatConfigAnswer,
  SurveyAnswer,
} from "@do/shared-types";
import axios from "axios";
import { chatHttp } from "./chatHttp";

// Query functions and mutations of the do-chat API routes, called through react-query.

export async function fetchDochatConfigs() {
  const { data } = await axios.get<{ id: string; configType: string }[]>("/api/configs");
  return data;
}

// The display configuration, and the conversation start parameters when the chat may start.
// Every answer with a session starts a new conversation, with a new `analyticId`.
export async function fetchDochatConfig(
  { id, digitalDynamicCtx, ...contextParams }: APIDochatConfig,
  ticket?: string | null
) {
  const { data } = await axios.get<APIDochatConfigAnswer>(
    `/api/configs/${encodeURIComponent(id)}`,
    {
      params: {
        ...contextParams,
        digitalDynamicCtx: digitalDynamicCtx ? JSON.stringify(digitalDynamicCtx) : null,
      },
      // The sign-in ticket, when the chat has one.
      headers: ticket ? { Authorization: `Bearer ${ticket}` } : undefined,
    }
  );
  return data;
}

export async function fetchOutOfCredits(analyticId: string) {
  const { data } = await chatHttp.get<{ outOfCredits: boolean }>(`/api/${analyticId}/credits`);
  return data.outOfCredits;
}

export async function fetchSessionEventTypes(analyticId: string) {
  const { data } = await chatHttp.get<AnalyticsEventType[]>(
    `/api/${analyticId}/session-event-types`
  );
  return data;
}

export async function submitSurvey({
  analyticId,
  surveyAnswer,
}: {
  analyticId: string;
  surveyAnswer: SurveyAnswer;
}) {
  await chatHttp.post(`/api/${analyticId}/survey`, { surveyAnswer });
}

export async function trackEvent({
  analyticId,
  eventType,
  properties,
}: {
  analyticId: string;
  eventType: AnalyticsEventType;
  properties?: Record<string, unknown>;
}) {
  await chatHttp.post(`/api/${analyticId}/track`, { eventType, properties });
}

// Created as soon as the login screen appears, so that the popup opens synchronously on click.
export async function createAuthTransaction(payload: {
  dochatConfigId: string;
  provider: DochatAuthProvider;
  locale: string;
}) {
  const { data } = await axios.post<{ txId: string; txSecret: string }>(
    "/api/auth/transactions",
    payload
  );
  return data;
}

export type WaitForSignInAnswer =
  | { status: "pending" }
  | { status: "completed"; ticket: string }
  | { status: "failed"; reason: DochatAuthPublicReason }
  | { status: "expired" };

// Held by the server for up to 25 s: about 3 requests per minute while the popup is open.
export async function waitForSignIn({ txId, txSecret }: { txId: string; txSecret: string }) {
  const { data } = await axios.post<WaitForSignInAnswer>(
    `/api/auth/transactions/${encodeURIComponent(txId)}/wait`,
    { txSecret },
    { timeout: 35_000 }
  );
  return data;
}
